import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ImageViewer from '../components/ImageViewer';
import { locationLabel } from '../utils/labels';
import './Guide.css';

// "Guide me": walks from the top-level storage location down to where an item (or a
// compartment) lives, one place per step, so a loose item can be put back.
function Guide() {
  const { kind, id } = useParams();
  const navigate = useNavigate();
  const isItem = kind === 'items';
  const [item, setItem] = useState(null);
  const [path, setPath] = useState([]);
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState('loading'); // loading | ready | unassigned | missing

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        let locationId = id;
        if (isItem) {
          const itemRes = await fetch(`/api/items/${id}`);
          if (!itemRes.ok) throw new Error('missing');
          const itemData = await itemRes.json();
          if (cancelled) return;
          setItem(itemData);
          if (!itemData.storageLocationId) {
            setStatus('unassigned');
            return;
          }
          locationId = itemData.storageLocationId;
        }
        const pathRes = await fetch(`/api/locations/${locationId}/path`);
        if (!pathRes.ok) throw new Error('missing');
        const chain = await pathRes.json();
        if (cancelled) return;
        setPath(chain);
        setStep(0);
        setStatus('ready');
      } catch (error) {
        if (!cancelled) setStatus('missing');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [kind, id, isItem]);

  const backTo = isItem ? `/items/${id}` : `/locations/${id}`;

  if (status === 'loading') {
    return <div className="guide"><p>Loading...</p></div>;
  }
  if (status === 'missing') {
    return <div className="guide"><p>Nothing to guide you to here.</p><Link to="/locations" className="btn btn-secondary">Back</Link></div>;
  }
  if (status === 'unassigned') {
    return (
      <div className="guide">
        <p>{item.name} is not assigned to a storage location yet, so there is nowhere to guide you.</p>
        <Link to={`/items/${id}/edit`} className="btn btn-primary">Choose a location</Link>
      </div>
    );
  }

  const here = path[step];
  const isFirst = step === 0;
  const isLast = step === path.length - 1;
  const heading = isFirst && !isLast ? 'Start here' : isLast ? (isItem ? 'Put it away here' : 'You have arrived') : 'Then go to';

  return (
    <div className="guide">
      <div className="guide-top">
        <Link to={backTo} className="guide-close">✕ Close</Link>
        <span className="guide-progress">Step {step + 1} of {path.length}</span>
      </div>

      {isItem && (
        <div className="guide-subject">
          {item.image && (
            <img
              src={`data:${item.imageMimeType};base64,${item.image}`}
              alt=""
              className="guide-subject-thumb"
            />
          )}
          <span>Putting away: <strong>{item.name}</strong></span>
        </div>
      )}

      <div className="guide-trail" aria-label="Path">
        {path.map((place, index) => (
          <span key={place.id} className={index === step ? 'current' : index < step ? 'done' : ''}>
            {index > 0 && <span className="guide-trail-sep"> › </span>}
            {place.name}
          </span>
        ))}
      </div>

      <div className="guide-card">
        <div className="guide-heading">{heading}</div>
        {here.image ? (
          <ImageViewer
            src={`data:${here.imageMimeType};base64,${here.image}`}
            alt={here.name}
            className="guide-image"
          />
        ) : (
          <div className="guide-image guide-placeholder" aria-hidden="true">{here.parentLocationId ? '🗄️' : '🏠'}</div>
        )}
        <div className="guide-kind">{locationLabel(here)}</div>
        <h1>{here.name}</h1>
        {here.description && <p className="guide-description">{here.description}</p>}
      </div>

      <div className="guide-nav">
        <button type="button" className="btn btn-secondary" onClick={() => setStep(step - 1)} disabled={isFirst}>
          ‹ Previous
        </button>
        {isLast ? (
          <button type="button" className="btn btn-primary" onClick={() => navigate(backTo)}>
            Done
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={() => setStep(step + 1)}>
            Next ›
          </button>
        )}
      </div>
    </div>
  );
}

export default Guide;
